/**
 * Tipos do domínio do PCP.
 *
 * Nomes em português, iguais aos da Ordem de Serviço da gráfica (PRD §3.4).
 * O que vem do PDF é guardado **como veio** (`nomeOs`, `equipamentoOs`); o que
 * o sistema interpreta mora em campo separado (`estacaoId`, `tipoLinha`).
 *
 * Convenções:
 *  - Toda data/hora é ISO 8601 com fuso ("2026-09-04T14:07:00-03:00").
 *  - Todo tempo de duração é em SEGUNDOS, inteiro. h:mm só na exibição.
 *  - Todo identificador é string (uuid), exceto o número da OS, que é o número
 *    impresso — a chave natural que a gráfica usa para falar.
 */

// ---------------------------------------------------------------------------
// Enumerações
// ---------------------------------------------------------------------------

/**
 * A seção de uma etapa — o agrupamento que a PRÓPRIA OS usa ("Pré-impressão",
 * "Impressão", "Acabamento", ou o que a empresa chamar).
 *
 * ⚠️ É TEXTO, e não uma lista fixa: outra empresa tem outras seções. Os três
 * nomes da Gráfica Boa União são o PRESET (`SECOES`), não a verdade.
 */
export type Secao = string;

/** As seções da fábrica padrão (preset). É ponto de partida, não regra. */
export const SECOES: readonly string[] = ['pre_impressao', 'impressao', 'acabamento'];

/**
 * O que a linha de etapa é, depois de classificada pelo de-para.
 *  - `operacao`: alguém executa; entra na fila do tablet.
 *  - `custo`: lançamento de custo (as linhas "Click …", que são a impressora
 *    digital cobrada por clique). Fica na ficha, fora da fila.
 *  - `ignorar`: não interessa ao PCP.
 */
export type TipoLinha = 'operacao' | 'custo' | 'ignorar';

export type StatusOs = 'em_fila' | 'em_producao' | 'pronta' | 'concluida' | 'cancelada';

export type StatusEtapa = 'fila' | 'em_execucao' | 'pausada' | 'concluida' | 'pulada';

export type TipoEvento =
  | 'inicio'
  | 'pausa'
  | 'retomada'
  | 'conclusao'
  | 'pulo'
  | 'estorno';

/** De onde a etapa veio. */
export type OrigemEtapa = 'os' | 'manual' | 'retrabalho';

export type OrigemEvento = 'tablet' | 'web' | 'sistema';

export type Perfil =
  | 'operador'
  | 'encarregado'
  | 'pcp'
  | 'vendedor'
  | 'expedicao'
  | 'direcao'
  | 'administrador';

export const PERFIS: readonly Perfil[] = [
  'operador', 'encarregado', 'pcp', 'vendedor', 'expedicao', 'direcao', 'administrador',
] as const;

export type StatusImportacao =
  | 'aplicada'
  | 'duplicada'
  | 'quarentena'
  | 'reprocessada';

export type TipoExpedicao = 'expedida' | 'retirada';

export type TipoAlerta =
  | 'os_atrasada'
  | 'os_em_risco'
  | 'etapa_acima_do_previsto'
  | 'etapa_parada'
  | 'os_alterada_em_producao'
  | 'estacao_a_classificar'
  | 'importacao_quarentena'
  | 'divergencia_erp'
  | 'dispositivo_sem_sincronizar'
  | 'agente_sem_enviar'
  | 'evento_recusado'
  | 'relogio_fora'
  /**
   * "Não é minha etapa": o operador diz que aquela etapa não pertence à
   * estação dele. Não move nada — só chama o encarregado, que é quem sabe
   * para onde vai. Mover sozinho, pelo palpite de quem está na máquina, é
   * como o material se perde.
   */
  | 'etapa_no_lugar_errado';

// ---------------------------------------------------------------------------
// Cadastros
// ---------------------------------------------------------------------------

/**
 * O PAPEL do setor no fluxo — e não o nome dele.
 *
 * ⚠️ NÃO decida comportamento pelo NOME do setor ("Expedição", "Impressão").
 * Outra empresa chama os setores de outro jeito. O papel é configurável e é o
 * que o sistema lê. `outro` é o padrão: um setor comum de produção.
 */
export type PapelSetor = 'pre_impressao' | 'impressao' | 'acabamento' | 'expedicao' | 'outro';

export const PAPEIS_SETOR: readonly PapelSetor[] = [
  'pre_impressao',
  'impressao',
  'acabamento',
  'expedicao',
  'outro',
] as const;

export interface Setor {
  id: string;
  nome: string;
  ordem: number;
  /** Cor do sistema para o cartão e o quadro. Ex.: "var(--sys-blue)". */
  cor: string;
  /** O que este setor é no fluxo. É o que o código lê — nunca o nome. */
  papel: PapelSetor;
  ativo: boolean;
}

export interface Estacao {
  id: string;
  setorId: string;
  nome: string;
  /** Código curto para caber no cartão do tablet. */
  codigo: string;
  /** Quantas etapas podem estar em execução ao mesmo tempo nesta estação. */
  capacidadeParalela: number;
  ativa: boolean;
}

/**
 * Como o texto da OS vira estação (PRD RN-005).
 * O casamento é por (secao, equipamentoOs, nomeEtapa), do mais específico para
 * o mais genérico — `nomeEtapa` nulo casa qualquer etapa daquele equipamento.
 */
export interface DeParaEstacao {
  id: string;
  secao: Secao | null;
  /** Texto da coluna "Equip./Fornecedor". Nulo casa etapas sem equipamento. */
  equipamentoOs: string | null;
  /** Texto da coluna do nome da etapa. Nulo casa qualquer nome. */
  nomeEtapa: string | null;
  estacaoId: string | null;
  tipoLinha: TipoLinha;
  aprendidoEm: string | null;
  aprendidoPor: string | null;
}

export type CategoriaMotivo =
  | 'maquina'
  | 'material'
  | 'arte'
  | 'espera'
  | 'pessoal'
  | 'outro';

export interface MotivoPausa {
  id: string;
  codigo: string;
  descricao: string;
  categoria: CategoriaMotivo;
  exigeTexto: boolean;
  ativo: boolean;
}

/** Um dia da semana no calendário de trabalho. 0 = domingo. */
export interface DiaCalendario {
  diaSemana: number;
  trabalha: boolean;
  /** "08:00" */
  inicio: string;
  /** "18:00" */
  fim: string;
  /** Intervalo não produtivo, ex.: almoço. */
  pausaInicio: string | null;
  pausaFim: string | null;
}

export interface Calendario {
  dias: DiaCalendario[];
  /** Datas "AAAA-MM-DD" sem produção. */
  feriados: string[];
}

export interface Pessoa {
  id: string;
  nome: string;
  perfil: Perfil;
  /** Hash do PIN. O PIN em claro nunca é guardado nem sincronizado. */
  pinHash: string | null;
  /** Setores em que pode apontar. Vazio = todos (para perfis de escritório). */
  setores: string[];
  /** Conta de login, quando a pessoa usa o escritório. */
  usuarioId: string | null;
  ativa: boolean;
}

export interface Dispositivo {
  id: string;
  nome: string;
  setorId: string | null;
  estacaoPadraoId: string | null;
  ultimoSyncEm: string | null;
  versaoApp: string | null;
  pendentes: number;
  ativo: boolean;
  /** Janelas sem conexão medidas pelo próprio aparelho (PRD §12.2). */
  janelasOffline: JanelaOffline[];
}

export interface JanelaOffline {
  inicio: string;
  fim: string | null;
}

export interface Cliente {
  id: string;
  /** Código do cliente no sistema atual da gráfica ("03880"). */
  codigoErp: string;
  nome: string;
  nomeCurto: string;
}

// ---------------------------------------------------------------------------
// A Ordem de Serviço
// ---------------------------------------------------------------------------

export interface Contato {
  nome: string | null;
  telefone: string | null;
  email: string | null;
}

export interface Orcamento {
  numero: string;
  item: string;
  vendedor: string;
}

export interface Os {
  id: string;
  /** O número impresso na OS. Chave natural. */
  numero: number;
  versaoAtual: number;
  clienteId: string | null;
  clienteCurto: string;
  quantidade: number;
  produto: string;

  emitidaEm: string;
  emitidaPor: string;
  alteradaEm: string | null;
  alteradaPor: string | null;
  previsaoEntregaEm: string;

  orcamento: Orcamento | null;
  contato: Contato | null;
  transportadora: string | null;
  observacoesGerais: string[];

  entregaTipo: string | null;
  notaTipo: string | null;
  arquivar: string | null;
  ordemCompraCliente: string | null;
  acabamentoVinil: string | null;
  ultimaOsClienteEm: string | null;

  /** O carimbo "<< OS CONCLUÍDA >>" veio no PDF. */
  concluidaNoErp: boolean;

  status: StatusOs;
  /** Quando foi fixada como prioridade. Nulo = sem prioridade manual. */
  prioridadeEm: string | null;
  prontaEm: string | null;
  concluidaEm: string | null;
  canceladaEm: string | null;
  motivoCancelamento: string | null;

  /** Caminhos no armazenamento. */
  pdfRef: string | null;
  imagemFacaRef: string | null;
}

export interface OsVersao {
  id: string;
  osNumero: number;
  versao: number;
  hashTexto: string;
  importacaoId: string | null;
  versaoLeitor: string;
  diffResumo: string[];
  aplicadaEm: string;
}

export interface OsPlano {
  id: string;
  osNumero: number;
  numero: number;
  tipo: 'cores' | 'pags_cores_lams';
  cores: string | null;
  paginas: number | null;
  laminas: string | null;
  formatoFinal: string | null;
  formatoFinalUnidade: 'cm' | 'mm' | null;
  pecasPorFolha: number | null;
  materialCodigo: string | null;
  materialFormato: string | null;
  montagem: string | null;
  tiraRetira: boolean;
  impressoraCodigo: string | null;
  abertura: string | null;
  refileSangria: string | null;
  formatoImpressao: string | null;
  faca: string | null;
  lombada: string | null;
  solda: boolean;
  outrosDetalhes: string | null;
}

export interface OsEtapa {
  id: string;
  osNumero: number;
  secao: Secao;
  /** Ordem impressa dentro da seção. */
  sequencia: number;
  /** Nome como veio na OS. */
  nomeOs: string;
  plano: number | null;
  /** Coluna "Equip./Fornecedor", como veio. */
  equipamentoOs: string | null;

  estacaoId: string | null;
  setorId: string | null;
  tipoLinha: TipoLinha;
  /** Nenhum de-para casou; o encarregado precisa classificar (RN-005). */
  aClassificar: boolean;

  impressoesPrevistas: number | null;
  unidade: string | null;
  tempoPrevistoSeg: number | null;
  observacoesOs: string | null;

  origem: OrigemEtapa;
  /** Para retrabalho: a etapa que deu origem. */
  etapaOrigemId: string | null;
  versaoCriada: number;
  /** A OS mudou depois que esta etapa começou (RN-003). */
  marcadaVerificar: boolean;
  /** Posição escolhida à mão na fila da estação. */
  ordemManual: number | null;
}

export interface OsMaterial {
  id: string;
  osNumero: number;
  /**
   * Posição na lista impressa da OS.
   *
   * O identificador é aleatório e o banco devolve por ele: sem esta coluna, a
   * ficha mostraria "Cola Branca" antes do papel, numa ordem diferente a cada
   * gráfica. A lista de materiais é lida em voz alta na hora de separar — a
   * ordem do papel importa.
   */
  ordem: number;
  codigo: string | null;
  nome: string;
  plano: number | null;
  quantidade: number;
  unidade: string;
  quebraPct: number | null;
  observacoes: string | null;
}

export interface OsEntregaPrevista {
  id: string;
  osNumero: number;
  bruto: string;
}

// ---------------------------------------------------------------------------
// Produção
// ---------------------------------------------------------------------------

/**
 * Evento de produção. Imutável (RN-020).
 *
 * O `id` nasce no aparelho junto com a ação — nunca no envio. É isso que faz
 * reenviar ser inofensivo (RN-042, lição do Nexo em Campo).
 */
export interface EventoProducao {
  id: string;
  osEtapaId: string;
  tipo: TipoEvento;
  pessoaId: string | null;
  dispositivoId: string | null;
  estacaoId: string | null;

  qtdBoa: number | null;
  qtdRefugo: number | null;
  motivoId: string | null;
  texto: string | null;

  /** Quando aconteceu, pelo relógio do aparelho. */
  ocorridoEm: string;
  /** Quando o servidor recebeu. Nulo enquanto está na fila local. */
  recebidoEm: string | null;
  desvioRelogioSeg: number | null;

  origem: OrigemEvento;
  /** Para `tipo: 'estorno'`: o evento anulado. */
  estornaEventoId: string | null;
  fotoRef: string | null;
}

export interface Expedicao {
  id: string;
  osNumero: number;
  tipo: TipoExpedicao;
  quemLevou: string | null;
  transportadora: string | null;
  pessoaId: string | null;
  ocorridoEm: string;
  observacao: string | null;
}

// ---------------------------------------------------------------------------
// Importação e alertas
// ---------------------------------------------------------------------------

export interface Importacao {
  id: string;
  origem: 'agente' | 'upload';
  arquivo: string;
  hashPdf: string;
  hashTexto: string | null;
  recebidoEm: string;
  status: StatusImportacao;
  osNumero: number | null;
  versao: number | null;
  versaoLeitor: string;
  erro: string | null;
  avisos: string[];
  pdfRef: string | null;
  por: string | null;
}

export interface Alerta {
  id: string;
  tipo: TipoAlerta;
  osNumero: number | null;
  osEtapaId: string | null;
  dispositivoId: string | null;
  importacaoId: string | null;
  texto: string;
  criadoEm: string;
  resolvidoEm: string | null;
  resolvidoPor: string | null;
}

// ---------------------------------------------------------------------------
// Parâmetros
// ---------------------------------------------------------------------------

/**
 * Tudo que varia e não deve virar constante no código.
 * Os valores de partida estão em `parametros.ts`; a gráfica ajusta na tela.
 */
export interface Parametros {
  /** Quanto a quantidade apontada pode passar da prevista antes de virar revisão. */
  toleranciaQuantidadePct: number;
  /** Acima disto o alerta de etapa demorada dispara. 150 = 150% do previsto. */
  alertaEtapaPct: number;
  /** Horas em fila sem começar, com estação livre, antes de alertar. */
  alertaEtapaParadaHoras: number;
  /** Minutos de inatividade até o tablet pedir o PIN de novo. */
  inatividadeTabletMin: number;
  /** Desvio de relógio que gera aviso. */
  desvioRelogioAvisoSeg: number;
  /** O acabamento só entra na fila quando todas as impressões concluírem. */
  acabamentoEsperaImpressoes: boolean;
  /** Um operador pode estar em mais de uma etapa ao mesmo tempo. */
  operadorEmVariasEtapas: boolean;
  /** O operador vê as OS de todos os setores (ver, não apontar). */
  operadorVeTodosSetores: boolean;
  /** A OS deixa de contar como atrasada quando fica pronta, ou só ao ser entregue. */
  atrasoContaAte: 'pronta' | 'concluida';
  /** Dias de OS concluída que continuam no conjunto de trabalho do tablet. */
  diasNoConjuntoDeTrabalho: number;
}

// ---------------------------------------------------------------------------
// Estados derivados (nunca gravados: calculados a partir dos eventos)
// ---------------------------------------------------------------------------

export interface EstadoEtapa {
  status: StatusEtapa;
  iniciadaEm: string | null;
  concluidaEm: string | null;
  /** Soma dos intervalos em execução, em segundos. Pausa não conta (RN-022). */
  tempoRealSeg: number;
  qtdBoa: number | null;
  qtdRefugo: number | null;
  /** Pessoas que registraram início ou retomada e ainda não concluíram. */
  operadores: string[];
  /** Motivo da pausa atual, quando pausada. */
  motivoPausaId: string | null;
  /** Marcas de revisão levantadas pela derivação (RN-023, RN-040). */
  marcas: MarcaEtapa[];
}

export type MarcaEtapa =
  | 'quantidade_acima_do_previsto'
  | 'conclusao_duplicada'
  | 'inicio_concorrente'
  | 'encerrada_automaticamente';

export interface EstadoOs {
  status: StatusOs;
  prontaEm: string | null;
  /** Etapas de operação concluídas ou puladas. */
  etapasConcluidas: number;
  etapasTotal: number;
  /** Soma do tempo previsto das etapas que ainda não terminaram. */
  tempoPrevistoRestanteSeg: number;
  tempoPrevistoTotalSeg: number;
  tempoRealTotalSeg: number;
  /** Onde a OS está agora: a primeira etapa em execução, ou a próxima da fila. */
  etapaAtualId: string | null;
}
